import { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Coffee,
  Gift,
  Home,
  PawPrint,
  Scissors,
  Shirt,
  Sparkles,
  Store,
  Utensils,
  Wrench
} from 'lucide-react'
import { CatalogBenefits, CatalogFaq, CatalogHeroSection, CatalogPricing, CatalogValueCards, SegmentSelector } from './components/SegmentedCatalogSections'
import LandingMainHeader from './LandingMainHeader'

const WHATSAPP_NUMBER = '573124260606'

const segments = {
  'regalos-desayunos': {
    eyebrow: 'Catalogo para regalos y desayunos sorpresa',
    title: 'Muestra tus detalles en un catalogo que vende por WhatsApp',
    subtitle: 'Organiza desayunos, anchetas, globos, flores y detalles personalizados en un link claro, visual y facil de enviar.',
    icon: Gift,
    gradient: 'from-fuchsia-600 via-rose-500 to-orange-400',
    audience: 'Emprendimientos de regalos, desayunos sorpresa, floristerias y detalles personalizados.',
    pain: 'Tus clientes preguntan por opciones, precios, fechas, colores y fotos antes de decidir.',
    promise: 'Un catalogo con videos cortos ayuda a que el cliente vea opciones por ocasion y te escriba con una idea mas clara.',
    products: ['Desayunos sorpresa', 'Anchetas', 'Ramos', 'Globos', 'Cajas personalizadas', 'Detalles por fecha especial'],
    benefits: ['Opciones por ocasion y presupuesto', 'Menos fotos sueltas por WhatsApp', 'Pedido directo con mensaje prearmado', 'Ideal para fechas especiales'],
    cta: 'Quiero mi catalogo de regalos',
    seoTitle: 'Catalogo digital para regalos y desayunos sorpresa | AppEmprendedor',
    seoDescription: 'Crea un video catalogo para regalos, desayunos sorpresa, anchetas y detalles con pedido directo por WhatsApp. Planes desde $50.000 mensuales.',
    seoKeywords: 'catalogo digital regalos, desayunos sorpresa WhatsApp, catalogo para anchetas, video catalogo regalos'
  },
  'postres-reposteria': {
    eyebrow: 'Catalogo para postres y reposteria',
    title: 'Convierte tus tortas y postres en pedidos por WhatsApp',
    subtitle: 'Presenta sabores, tamanos, porciones, combos y productos especiales con videos que despiertan antojo.',
    icon: Coffee,
    gradient: 'from-amber-500 via-orange-500 to-red-500',
    audience: 'Reposterias, pastelerias, negocios de postres, heladerias y panaderias artesanales.',
    pain: 'El cliente quiere ver presentacion, tamanos, sabores y precios antes de comprar.',
    promise: 'Tu catalogo deja todo organizado para que el cliente elija mas rapido y pida directo por WhatsApp.',
    products: ['Tortas', 'Postres', 'Cupcakes', 'Galletas', 'Helados', 'Combos dulces'],
    benefits: ['Videos que generan antojo', 'Categorias por sabor o ocasion', 'Facil de compartir en redes', 'Reduce preguntas repetidas'],
    cta: 'Quiero vender mas postres',
    seoTitle: 'Catalogo digital para postres y reposteria | AppEmprendedor',
    seoDescription: 'Video catalogo para tortas, postres, helados y reposteria con productos organizados y pedidos por WhatsApp.',
    seoKeywords: 'catalogo digital postres, catalogo reposteria WhatsApp, vender tortas por WhatsApp, video catalogo pasteleria'
  },
  'restaurantes-comidas-rapidas': {
    eyebrow: 'Menu digital para restaurantes',
    title: 'Un menu visual con pedido directo por WhatsApp',
    subtitle: 'Muestra platos, combos, bebidas y promociones en un link profesional listo para compartir.',
    icon: Utensils,
    gradient: 'from-red-600 via-orange-500 to-yellow-400',
    audience: 'Restaurantes, comidas rapidas, cafes, parrillas, pizzerias y negocios con domicilios.',
    pain: 'Tus clientes piden carta, precios, fotos, combos y horarios antes de ordenar.',
    promise: 'Un menu con videos y categorias hace que el cliente decida mas rapido y llegue al WhatsApp con el pedido armado.',
    products: ['Platos fuertes', 'Combos', 'Hamburguesas', 'Pizzas', 'Bebidas', 'Promociones'],
    benefits: ['Menu ordenado por categorias', 'Link para Instagram y estados', 'Pedido directo por WhatsApp', 'Promos faciles de destacar'],
    cta: 'Quiero mi menu digital',
    seoTitle: 'Menu digital para restaurantes con pedidos por WhatsApp | AppEmprendedor',
    seoDescription: 'Menu digital visual para restaurantes, comidas rapidas, cafes y domicilios. Muestra platos, combos y promociones con pedido por WhatsApp.',
    seoKeywords: 'menu digital restaurante, carta digital WhatsApp, menu comidas rapidas, pedidos por WhatsApp restaurante'
  },
  'ropa-boutiques': {
    eyebrow: 'Catalogo para ropa y boutiques',
    title: 'Muestra prendas, tallas y estilos sin enviar fotos una por una',
    subtitle: 'Crea un catalogo visual para que tus clientes vean referencias, colores, tallas y disponibilidad antes de escribir.',
    icon: Shirt,
    gradient: 'from-violet-600 via-indigo-500 to-sky-400',
    audience: 'Boutiques, tiendas de ropa femenina, ropa infantil, calzado, accesorios y moda local.',
    pain: 'El cliente pregunta por tallas, colores, precio y mas fotos antes de decidir.',
    promise: 'Tus prendas quedan organizadas en un link con videos o fotos, descripcion y boton de pedido.',
    products: ['Blusas', 'Vestidos', 'Jeans', 'Calzado', 'Accesorios', 'Promociones'],
    benefits: ['Colecciones por temporada', 'Videos que muestran caida y textura', 'Pedido con referencia clara', 'Ideal para publicar en historias'],
    cta: 'Quiero mi catalogo de ropa',
    seoTitle: 'Catalogo digital para ropa y boutiques | AppEmprendedor',
    seoDescription: 'Catalogo digital para tiendas de ropa, boutiques, calzado y accesorios con fotos, videos, tallas y pedidos por WhatsApp.',
    seoKeywords: 'catalogo digital ropa, catalogo boutique WhatsApp, vender ropa por WhatsApp, catalogo moda online'
  },
  'belleza-cosmetica': {
    eyebrow: 'Catalogo para belleza y cosmetica',
    title: 'Vende productos de belleza con explicaciones claras y videos cortos',
    subtitle: 'Organiza maquillaje, cuidado facial, tratamientos, kits y promociones para vender mejor por WhatsApp.',
    icon: Sparkles,
    gradient: 'from-pink-600 via-fuchsia-500 to-purple-500',
    audience: 'Tiendas de belleza, cosmetica, skincare, distribuidores, salones y emprendimientos de cuidado personal.',
    pain: 'El cliente necesita entender tonos, usos, beneficios y presentaciones antes de comprar.',
    promise: 'El catalogo te ayuda a mostrar productos por categoria y recibir pedidos con informacion mas precisa.',
    products: ['Maquillaje', 'Skincare', 'Kits', 'Tratamientos', 'Capilares', 'Promociones'],
    benefits: ['Explica usos y beneficios', 'Categorias por necesidad', 'Mejor presentacion de kits', 'Menos audios repetidos'],
    cta: 'Quiero mi catalogo de belleza',
    seoTitle: 'Catalogo digital para belleza y cosmetica | AppEmprendedor',
    seoDescription: 'Catalogo para maquillaje, skincare, productos capilares y servicios de belleza con descripcion, videos y pedido por WhatsApp.',
    seoKeywords: 'catalogo digital belleza, catalogo cosmetica WhatsApp, vender maquillaje por WhatsApp, catalogo skincare'
  },
  'ferreterias-materiales': {
    eyebrow: 'Catalogo para ferreterias',
    title: 'Organiza herramientas, materiales y referencias para vender por WhatsApp',
    subtitle: 'Un catalogo practico para que el cliente encuentre productos, medidas, marcas y promociones sin pedir fotos sueltas.',
    icon: Wrench,
    gradient: 'from-slate-700 via-slate-600 to-orange-500',
    audience: 'Ferreterias, depositos, tiendas de herramientas, materiales de construccion e insumos.',
    pain: 'Los clientes preguntan por referencias, medidas, marcas, disponibilidad y precios todo el dia.',
    promise: 'Un catalogo por categorias facilita consultar productos y enviar pedidos mas claros por WhatsApp.',
    products: ['Herramientas', 'Cementos', 'Pinturas', 'Electricos', 'Plomeria', 'Promociones'],
    benefits: ['Categorias faciles de navegar', 'Referencias claras', 'Promos visibles', 'Pedido con producto identificado'],
    cta: 'Quiero catalogo para mi ferreteria',
    seoTitle: 'Catalogo digital para ferreterias y materiales | AppEmprendedor',
    seoDescription: 'Catalogo digital para ferreterias, herramientas, materiales, electricos y plomeria con referencias y pedidos por WhatsApp.',
    seoKeywords: 'catalogo digital ferreteria, catalogo materiales WhatsApp, ferreteria online WhatsApp, catalogo herramientas'
  },
  'pet-shops-mascotas': {
    eyebrow: 'Catalogo para pet shops',
    title: 'Vende alimento, juguetes y accesorios para mascotas en un link ordenado',
    subtitle: 'Tus clientes pueden ver marcas, tamanos, precios, combos y disponibilidad antes de pedir por WhatsApp.',
    icon: PawPrint,
    gradient: 'from-emerald-600 via-teal-500 to-cyan-400',
    audience: 'Pet shops, tiendas de mascotas, veterinarias con productos y emprendimientos de accesorios.',
    pain: 'El cliente pregunta por marcas, tamanos, edad de la mascota, accesorios y disponibilidad.',
    promise: 'El catalogo organiza productos por tipo de mascota, marca o necesidad para acelerar el pedido.',
    products: ['Alimentos', 'Snacks', 'Juguetes', 'Camas', 'Accesorios', 'Higiene'],
    benefits: ['Productos por mascota o marca', 'Combos y promociones', 'Informacion mas clara', 'Pedido directo por WhatsApp'],
    cta: 'Quiero catalogo para mascotas',
    seoTitle: 'Catalogo digital para pet shops y mascotas | AppEmprendedor',
    seoDescription: 'Catalogo digital para pet shops, alimentos, juguetes, accesorios y productos para mascotas con pedidos por WhatsApp.',
    seoKeywords: 'catalogo digital pet shop, catalogo mascotas WhatsApp, vender productos mascotas, tienda mascotas WhatsApp'
  },
  'turismo-hoteles': {
    eyebrow: 'Catalogo para turismo y hoteles',
    title: 'Muestra experiencias, habitaciones y paquetes con videos que convencen',
    subtitle: 'Ideal para hoteles, pasadias, restaurantes campestres, fincas, glamping y experiencias turisticas.',
    icon: Home,
    gradient: 'from-sky-700 via-blue-500 to-emerald-400',
    audience: 'Hoteles, glampings, fincas, pasadias, parques, restaurantes campestres y operadores turisticos.',
    pain: 'El cliente pregunta por fotos, precios, paquetes, disponibilidad, ubicacion y que incluye.',
    promise: 'Un catalogo visual muestra experiencias y servicios en un link que lleva a reserva por WhatsApp.',
    products: ['Habitaciones', 'Pasadias', 'Paquetes', 'Restaurante', 'Actividades', 'Promociones'],
    benefits: ['Videos de espacios y experiencias', 'Paquetes claros', 'Boton de reserva', 'Ideal para temporadas'],
    cta: 'Quiero catalogo turistico',
    seoTitle: 'Catalogo digital para hoteles, turismo y pasadias | AppEmprendedor',
    seoDescription: 'Catalogo visual para hoteles, glamping, fincas, pasadias y experiencias turisticas con videos y reservas por WhatsApp.',
    seoKeywords: 'catalogo digital hoteles, catalogo turismo WhatsApp, reservas por WhatsApp, catalogo glamping'
  },
  'productos-locales-artesanias': {
    eyebrow: 'Catalogo para productos locales',
    title: 'Dale presentacion profesional a tus productos artesanales y locales',
    subtitle: 'Muestra alimentos, artesanias, regalos, productos regionales y combos con una vitrina digital facil de compartir.',
    icon: Store,
    gradient: 'from-lime-700 via-green-500 to-amber-400',
    audience: 'Productores locales, artesanos, tiendas regionales, marcas de alimentos y emprendimientos familiares.',
    pain: 'Tus productos necesitan historia, presentacion y confianza para vender fuera del punto fisico.',
    promise: 'El video-catalogo permite explicar origen, usos, presentaciones y precios con pedido directo.',
    products: ['Artesanias', 'Dulces', 'Cafe', 'Lacteos', 'Salsas', 'Kits regionales'],
    benefits: ['Cuenta mejor el producto', 'Facil de compartir en ferias', 'Combos por ocasion', 'Pedido directo por WhatsApp'],
    cta: 'Quiero catalogo de productos locales',
    seoTitle: 'Catalogo digital para productos locales y artesanias | AppEmprendedor',
    seoDescription: 'Video catalogo para artesanias, alimentos locales, cafe, lacteos, dulces y productos regionales con pedido por WhatsApp.',
    seoKeywords: 'catalogo productos locales, catalogo artesanias WhatsApp, vender productos artesanales, catalogo regional'
  },
  'servicios-estetica': {
    eyebrow: 'Catalogo para servicios de estetica',
    title: 'Presenta servicios, paquetes y resultados antes de agendar',
    subtitle: 'Un catalogo visual para barberias, peluquerias, unas, spa, cejas, maquillaje y estetica.',
    icon: Scissors,
    gradient: 'from-zinc-800 via-neutral-700 to-pink-500',
    audience: 'Salones, barberias, estudios de unas, cejas, maquillaje, spa y profesionales de belleza.',
    pain: 'El cliente pregunta por precios, duracion, estilos, paquetes y disponibilidad antes de agendar.',
    promise: 'El catalogo organiza servicios y paquetes para que el cliente llegue a WhatsApp listo para reservar.',
    products: ['Cortes', 'Unas', 'Cejas', 'Maquillaje', 'Spa', 'Paquetes'],
    benefits: ['Servicios por categoria', 'Precios desde o paquetes', 'Videos de resultados', 'Reserva por WhatsApp'],
    cta: 'Quiero catalogo de servicios',
    seoTitle: 'Catalogo digital para servicios de estetica y belleza | AppEmprendedor',
    seoDescription: 'Catalogo para peluquerias, barberias, unas, cejas, maquillaje y spa con servicios, paquetes y reservas por WhatsApp.',
    seoKeywords: 'catalogo servicios estetica, catalogo barberia WhatsApp, catalogo peluqueria, agenda belleza WhatsApp'
  }
}

const defaultSegment = segments['regalos-desayunos']

const plans = [
  {
    name: 'Plan Lanzamiento',
    badge: 'Para empezar rapido',
    setupPrice: '$149.000 COP',
    monthlyPrice: '$29.000 COP/mes',
    detail: 'Ideal para emprendedores, reposteria, accesorios, regalos pequenos y negocios que quieren validar rapido.',
    features: ['Hasta 15 productos', 'Fotos o videos', '3 categorias', 'Boton directo a WhatsApp', 'Link para Instagram, TikTok y estados', 'Entrega en 72 horas']
  },
  {
    name: 'Plan Emprendedor',
    badge: 'Mas vendido',
    highlighted: true,
    setupPrice: '$249.000 COP',
    monthlyPrice: '$49.000 COP/mes',
    detail: 'Recomendado para negocios que venden por WhatsApp todos los dias y necesitan un catalogo mas completo.',
    features: ['Hasta 40 productos', 'Hasta 6 categorias', 'Fotos o videos', 'Mensaje de pedido prellenado a WhatsApp', 'Diseno con colores de marca', '1 ronda de ajustes', 'Entrega en 48-72 horas']
  },
  {
    name: 'Plan Negocio',
    badge: 'Catalogos grandes',
    setupPrice: '$399.000 COP',
    monthlyPrice: '$79.000 COP/mes',
    detail: 'Ideal para restaurantes, ropa, licoreras, tiendas y negocios con inventario mas amplio.',
    features: ['Hasta 100 productos', 'Categorias ilimitadas razonables', 'Productos destacados', 'Combos y promociones', 'Diseno mas personalizado', 'Soporte prioritario', '2 rondas de ajustes']
  }
]

const launchOffer = {
  title: 'Promo primeros 10 clientes',
  price: '$199.000 COP unico pago',
  description: 'Incluye instalacion, montaje inicial y 2 meses de servicio. Despues continua por $49.000 COP/mes.'
}

const allSegments = Object.entries(segments).map(([slug, value]) => ({ slug, ...value }))

const segmentFaqs = (segment) => [
  {
    question: `Que incluye el catalogo digital para ${segment.eyebrow.toLowerCase().replace('catalogo para ', '').replace('menu digital para ', '')}?`,
    answer: `Incluye estructura del catalogo, carga inicial de productos o servicios, textos comerciales, organizacion por categorias y boton de pedido directo por WhatsApp.`
  },
  {
    question: 'El catalogo funciona con videos?',
    answer: 'Si. La propuesta esta pensada como video-catalogo: cada producto puede mostrarse con video corto, descripcion, precio y llamado a pedir por WhatsApp.'
  },
  {
    question: 'Sirve para vender por WhatsApp?',
    answer: 'Si. Cada producto queda orientado a iniciar la conversacion por WhatsApp para reducir preguntas repetidas y facilitar el pedido.'
  },
  {
    question: 'Ustedes ayudan a montarlo?',
    answer: 'Si. Todos los planes incluyen instalacion inicial con carga de productos segun el plan, organizacion por categorias, textos comerciales y configuracion del pedido por WhatsApp.'
  }
]

const buildWhatsAppLink = (segment) => {
  const text = `Hola, quiero mi catalogo online conectado a WhatsApp para ${segment.eyebrow.toLowerCase()}. Me interesa el plan de $249.000 COP o la oferta de lanzamiento.`
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`
}

export default function SegmentedCatalogLanding() {
  const { segmentSlug } = useParams()
  const currentSlug = segments[segmentSlug] ? segmentSlug : 'regalos-desayunos'
  const segment = segments[currentSlug] || defaultSegment
  const Icon = segment.icon
  const whatsappLink = buildWhatsAppLink(segment)
  const canonicalUrl = `https://appemprendedor.com/latam/soluciones/catalogo-emprendedor/${currentSlug}`
  const faqs = segmentFaqs(segment)
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: segment.seoTitle,
    description: segment.seoDescription,
    provider: {
      '@type': 'Organization',
      name: 'AppEmprendedor',
      url: 'https://appemprendedor.com'
    },
    areaServed: ['Colombia', 'Latinoamerica'],
    serviceType: 'Catalogo digital con pedidos por WhatsApp',
    offers: plans.map((plan) => ({
      '@type': 'Offer',
      name: plan.name,
      priceCurrency: 'COP',
      price: plan.setupPrice.replace(/[^0-9]/g, ''),
      description: `${plan.setupPrice} instalacion + ${plan.monthlyPrice}. ${plan.detail}`,
      availability: 'https://schema.org/InStock'
    })),
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  }

  useEffect(() => {
    document.title = segment.seoTitle

    const setMeta = (selector, attr, value) => {
      let element = document.head.querySelector(selector)
      if (!element) {
        element = document.createElement('meta')
        const match = selector.match(/meta\[(name|property)="([^"]+)"\]/)
        if (match) element.setAttribute(match[1], match[2])
        document.head.appendChild(element)
      }
      element.setAttribute(attr, value)
    }

    setMeta('meta[name="description"]', 'content', segment.seoDescription)
    setMeta('meta[name="keywords"]', 'content', segment.seoKeywords)
    setMeta('meta[property="og:title"]', 'content', segment.seoTitle)
    setMeta('meta[property="og:description"]', 'content', segment.seoDescription)
    setMeta('meta[property="og:type"]', 'content', 'website')
    setMeta('meta[property="og:url"]', 'content', canonicalUrl)
    setMeta('meta[property="og:site_name"]', 'content', 'AppEmprendedor')
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image')
    setMeta('meta[name="twitter:title"]', 'content', segment.seoTitle)
    setMeta('meta[name="twitter:description"]', 'content', segment.seoDescription)

    let canonical = document.head.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', canonicalUrl)

    let schema = document.head.querySelector('script[data-segmented-catalog-schema="true"]')
    if (!schema) {
      schema = document.createElement('script')
      schema.type = 'application/ld+json'
      schema.setAttribute('data-segmented-catalog-schema', 'true')
      document.head.appendChild(schema)
    }
    schema.textContent = JSON.stringify(schemaData)
  }, [canonicalUrl, segment, schemaData])

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <LandingMainHeader />
      <main>
        <CatalogHeroSection segment={segment} Icon={Icon} whatsappLink={whatsappLink} />
        <CatalogValueCards segment={segment} />
        <CatalogBenefits benefits={segment.benefits} />
        <CatalogPricing plans={plans} launchOffer={launchOffer} whatsappLink={whatsappLink} />
        <CatalogFaq faqs={faqs} />
        <SegmentSelector allSegments={allSegments} />
      </main>
    </div>
  )
}
